import { useState, useCallback, useEffect } from 'react';
import { api, type Email } from '../api/client';
import { usePolling } from '../hooks/usePolling';

const PRIORITY_LABEL: Record<string, string> = { urgent: '🔴 Urgent', high: '🟠 High', normal: '🟡 Normal', low: '🟢 Low' };
const ACTIONS = ['reply', 'archive', 'snooze', 'delete'] as const;
const ACTION_LABELS: Record<string, string> = { reply: '↩ Reply', archive: '📦 Archive', snooze: '⏰ Snooze', delete: '🗑 Delete' };

export default function EmailsScreen() {
  const [emails, setEmails] = useState<Email[]>([]);
  const [loading, setLoading] = useState(true);
  const [triaging, setTriaging] = useState<string | null>(null);

  const load = useCallback(async () => {
    try { setEmails(await api.emails()); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);
  usePolling(load);

  const triage = async (id: string, action: string) => {
    if (action === 'delete' && !confirm('Remove this email from your list?')) return;
    setTriaging(id);
    try {
      await api.triageEmail(id, action);
      setEmails(prev => prev.filter(e => e.id !== id));
    } finally { setTriaging(null); }
  };

  return (
    <div className="main-content">
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 12 }}>
        <h1 style={{ fontSize: 22, fontWeight: 800 }}>📧 Inbox</h1>
        {!loading && (
          <span style={{ color: 'var(--text-2)', fontSize: 14 }}>
            {emails.length} untriaged{emails.length === 0 ? ' — inbox zero! 🎉' : ''}
          </span>
        )}
      </div>

      {loading ? (
        <div className="card"><p style={{ color: 'var(--text-2)' }}>Loading…</p></div>
      ) : emails.length === 0 ? (
        <div className="card">
          <div className="empty-state">
            <div className="empty-state-emoji">📭</div>
            <p className="empty-state-text">All emails triaged! Sync Wardyn on your Mac for new ones.</p>
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {emails.map((email) => (
            <div key={email.id} className="card" style={{ padding: 0, overflow: 'hidden' }}>
              {/* Priority accent strip */}
              <div style={{
                height: 3,
                background: email.priority === 'urgent' ? 'var(--danger)'
                  : email.priority === 'high' ? '#FF8C42'
                  : email.priority === 'normal' ? 'var(--warning)'
                  : 'var(--success)',
              }} />
              <div style={{ padding: '16px 20px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                  <span className={`badge badge-${email.priority}`}>
                    {PRIORITY_LABEL[email.priority] ?? email.priority}
                  </span>
                  {email.receivedAt && (
                    <span style={{ fontSize: 12, color: 'var(--text-3)' }}>
                      {new Date(email.receivedAt * 1000).toLocaleString('en-GB', { hour: '2-digit', minute: '2-digit', day: 'numeric', month: 'short' })}
                    </span>
                  )}
                </div>
                <div style={{ fontWeight: 600, fontSize: 15, marginBottom: 4 }}>{email.subject}</div>
                <div style={{ color: 'var(--text-2)', fontSize: 13, marginBottom: 4 }}>{email.sender}</div>
                {email.snippet && (
                  <div style={{ color: 'var(--text-3)', fontSize: 13, fontStyle: 'italic', marginBottom: 8 }}>
                    {email.snippet}
                  </div>
                )}
                {email.ai_summary && (
                  <div className="ai-pill" style={{ marginBottom: 10 }}>
                    ✨ AI: {email.ai_summary}
                    {email.ai_action && ` · Suggested: ${email.ai_action}`}
                  </div>
                )}

                {triaging === email.id ? (
                  <p style={{ color: 'var(--text-2)', fontSize: 13 }}>Processing…</p>
                ) : (
                  <div className="triage-actions">
                    {ACTIONS.map((action) => (
                      <button key={action} className={`triage-btn ${action}`} onClick={() => triage(email.id, action)}>
                        {ACTION_LABELS[action]}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
